import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import { ContentExtractionService } from "@/services/knowledgebase/ContentExtractionService";
import {
  validateVideoFile,
  validateVideoFileSize,
  getErrorMessage,
  formatFileSize,
} from "@/hooks/uploads/uploadValidation";
import { uploadFileToStorage } from "@/lib/uploadFileToStorage";
import { useAuth } from "@/auth/AuthProvider";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

// YouTube has its own dedicated upload method (transcript extraction, not
// ffmpeg transcription), so it's redirected there rather than submitted here.
const isYouTubeUrl = (url: URL) =>
  url.hostname.includes("youtube.com") || url.hostname.includes("youtu.be");

// Share-page hosts (Google Drive, Dropbox, OneDrive, Vimeo) are supported by
// the transcription service directly, but a direct file link is more
// reliable, so we show a soft hint rather than blocking submission.
const SHARE_PAGE_HOSTS: Array<(url: URL) => boolean> = [
  (url) => url.hostname.includes("drive.google.com"),
  (url) => url.hostname.includes("dropbox.com") && !url.hostname.startsWith("dl."),
  (url) => url.hostname.includes("onedrive.live.com") || url.hostname.includes("1drv.ms"),
  (url) => url.hostname.includes("vimeo.com"),
];

function detectYouTubeBlock(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);
    if (isYouTubeUrl(url)) {
      return 'This is a YouTube link — use the "YouTube" upload method instead, it extracts the transcript directly.';
    }
    return null;
  } catch {
    return null;
  }
}

function detectSharePageHint(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);
    if (isYouTubeUrl(url)) return null;
    if (SHARE_PAGE_HOSTS.some((test) => test(url))) {
      return "Better to use a direct link to the video file for the most reliable results.";
    }
    return null;
  } catch {
    return null;
  }
}

interface UploadVideoProps extends UploadComponentProps {
  videoFile: File | null;
  onVideoFileChange: (file: File | null) => void;
  videoUrl: string;
  onVideoUrlChange: (url: string) => void;
}

const UploadVideo = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  videoFile,
  onVideoFileChange,
  videoUrl,
  onVideoUrlChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadVideoProps) => {
  const { user } = useAuth();
  const videoTranscribeMutation = useUploadMutation("video", selectedAgentId, onUploadSuccess);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isSubmittingUrl, setIsSubmittingUrl] = useState(false);
  const [mode, setMode] = useState<"file" | "url">("file");

  const youTubeBlockMessage = useMemo(() => detectYouTubeBlock(videoUrl), [videoUrl]);
  const sharePageHint = useMemo(() => detectSharePageHint(videoUrl), [videoUrl]);

  // Videos can be GB-scale, so the file always goes client -> Storage directly first.
  // The edge function only ever receives a signed URL, never the raw bytes, to avoid
  // hitting Supabase Edge Function worker memory/resource limits.
  const uploadVideoFile = useCallback(
    async (file: File) => {
      try {
        setIsUploadingVideo(true);
        const uploadToast = toast.loading(`Uploading "${file.name}"...`);

        const { fileUrl } = await uploadFileToStorage(file, "temp-uploads");

        toast.dismiss(uploadToast);

        if (onDeferredActionAdd) {
          onDeferredActionAdd({
            type: "video",
            data: {
              fileUrl,
              fileName: file.name,
            },
          });
          toast.success(`"${file.name}" uploaded and queued for transcription`);
          onVideoFileChange(null);
        } else {
          videoTranscribeMutation.mutate({ fileUrl, fileName: file.name });
          onVideoFileChange(null);
        }
      } catch (error) {
        toast.error(`Failed to upload "${file.name}"`);
        console.error("Upload error:", error);
      } finally {
        setIsUploadingVideo(false);
      }
    },
    [onDeferredActionAdd, onVideoFileChange, videoTranscribeMutation],
  );

  const handleVideoFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!validateVideoFile(file)) {
        toast.error(getErrorMessage("video"));
        return;
      }

      if (!validateVideoFileSize(file)) {
        toast.error(getErrorMessage("video"));
        return;
      }

      onVideoFileChange(file);
      e.target.value = "";
    },
    [onVideoFileChange],
  );

  const handleUrlSubmit = useCallback(async () => {
    if (!videoUrl) {
      toast.error("Please enter a video URL");
      return;
    }

    try {
      new URL(videoUrl);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    if (youTubeBlockMessage) {
      toast.error("Use the YouTube upload method instead", {
        description: youTubeBlockMessage,
        duration: 8000,
      });
      return;
    }

    if (!user?.id) return;

    if (onDeferredActionAdd) {
      onDeferredActionAdd({
        type: "video",
        data: {
          fileUrl: videoUrl,
          fileName: videoUrl.split("/").pop() || "External Video",
          isExternalUrl: true,
        },
      });
      toast.success("Video URL queued for transcription");
      onVideoUrlChange("");
      return;
    }

    try {
      setIsSubmittingUrl(true);
      await ContentExtractionService.extractVideoFromExternalUrl(
        videoUrl,
        user.id,
        selectedAgentId && selectedAgentId !== "none" ? selectedAgentId : undefined,
      );
      toast.info("Video submitted. Transcription in progress - check back soon.");
      onVideoUrlChange("");
      onUploadSuccess?.();
    } catch (error) {
      toast.error("Failed to process video URL");
      console.error("Video URL submit error:", error);
    } finally {
      setIsSubmittingUrl(false);
    }
  }, [videoUrl, user?.id, selectedAgentId, onDeferredActionAdd, onVideoUrlChange, onUploadSuccess]);

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon
          name="clapperboard"
          size={64}
          color="#4E9CFF"
          gradientEnabled
          className="mb-4"
        />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Transcribe Video</h3>
        <p className="text-sm text-gray-600 mb-4 text-center">
          {mode === "file"
            ? "Upload a video file to extract its transcript"
            : "Paste a link to a video hosted elsewhere to extract its transcript"}
        </p>

        <div className="flex gap-1.5 mb-5 p-1 bg-gray-100 rounded-lg">
          <button
            type="button"
            onClick={() => setMode("file")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === "file" ? "bg-white shadow-sm text-genie-primary" : "text-gray-500"
            }`}
          >
            Upload File
          </button>
          <button
            type="button"
            onClick={() => setMode("url")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === "url" ? "bg-white shadow-sm text-genie-primary" : "text-gray-500"
            }`}
          >
            Video URL
          </button>
        </div>

        {mode === "file" ? (
          <>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoFileChange}
              className="hidden"
              id="video-file-input"
            />
            <label
              htmlFor="video-file-input"
              className="flex items-center justify-center w-full max-w-md h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-genie-primary transition-colors bg-gray-50"
            >
              {videoFile ? (
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">{videoFile.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatFileSize(videoFile.size)}</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Click to upload video file</p>
                  <p className="text-xs text-gray-500 mt-1">MP4, MOV, WebM, or MKV, up to 50MB</p>
                </div>
              )}
            </label>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Larger file? Switch to "Video URL" above.
            </p>
            {videoFile && (
              <Button
                onClick={() => uploadVideoFile(videoFile)}
                disabled={!videoFile || videoTranscribeMutation.isPending || isUploadingVideo}
                className="mt-4 bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-8 h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
              >
                {videoTranscribeMutation.isPending || isUploadingVideo ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {isUploadingVideo ? "Uploading..." : "Transcribing..."}
                  </>
                ) : (
                  onDeferredActionAdd ? "Upload Video" : "Transcribe Video"
                )}
              </Button>
            )}
          </>
        ) : (
          <div className="w-full">
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Input
                type="url"
                placeholder="https://example.com/video.mp4"
                value={videoUrl}
                onChange={(e) => onVideoUrlChange(e.target.value)}
                className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
                disabled={isSubmittingUrl}
              />
              <Button
                onClick={handleUrlSubmit}
                disabled={isSubmittingUrl || !videoUrl.trim()}
                className="w-full sm:w-auto bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-6 sm:px-8 h-11 sm:h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
              >
                {isSubmittingUrl ? <Loader2 className="h-5 w-5 animate-spin" /> : "Transcribe"}
              </Button>
            </div>
            {youTubeBlockMessage ? (
              <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">{youTubeBlockMessage}</p>
              </div>
            ) : sharePageHint ? (
              <p className="text-xs text-gray-500 mt-3 text-center">{sharePageHint}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-3 text-center">
                Better to use a direct link to the video file.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default UploadVideo;
